/**
 * Microphone capture pipeline.
 *
 * Owns the chain: `getUserMedia → MediaStreamAudioSource → AudioWorklet
 * (capture-processor) → main-thread Opus encode → transport.sendAudio`.
 *
 * The worklet runs on the audio thread and does the rate conversion
 * (device → 16 kHz mono) and 20 ms framing in fixed-point–friendly
 * arithmetic; the main thread does only the heavy Opus encode and the
 * `transport.sendAudio` call. This split keeps the audio callback free
 * of WASM calls and GC pressure.
 *
 * Frames flow continuously from the worklet to the main thread. The
 * transmit gate lives on the main thread: while transmitting, frames
 * are encoded and sent; while not, they are simply dropped after the
 * RMS reading is taken. This costs ~32 KB/s of cross-thread copies
 * (50 frames/sec × 640 bytes) — a fraction of what one Opus packet
 * encode does — and lets us drive a VU meter at all times so the user
 * can see their mic is alive before they press the button.
 *
 * The capture handle is intentionally minimal: [`startTransmitting`]
 * and [`stopTransmitting`] are the only two verbs the channel screen
 * needs, and [`destroy`] for teardown when the screen unmounts.
 *
 * **Lifecycle is decided by the caller.** The channel screen calls
 * [`startCapture`] on PTT-press and [`destroy`] on PTT-release —
 * mic per press, not per session. This is what keeps the OS in
 * media-routing mode between presses: holding a microphone open
 * forces iOS to route output through the earpiece, flips Bluetooth
 * headsets and car stereos into the narrow-band HFP profile, and
 * does similar on Android. Closing the mic stream lets the OS
 * restore loudspeaker / A2DP. The cost is a few hundred ms of
 * `getUserMedia` startup at the leading edge of each press, which
 * overlaps the floor-grant round-trip.
 */

import {
    OPUS_FRAME_DURATION_MS,
    OPUS_FRAME_SAMPLES,
    OpusEncoder,
} from "./opus";

/** Constructor options for [`startCapture`]. */
export interface CaptureOptions {
    /**
     * Identifier of the input device to capture from, as returned by
     * `navigator.mediaDevices.enumerateDevices()`. When omitted the
     * browser's default microphone is used. An invalid id falls
     * through to whatever `getUserMedia` resolves the constraint to —
     * typically the default — rather than throwing.
     */
    deviceId?: string;
    /**
     * Called once per encoded 20 ms frame, on the main thread, while
     * transmission is active. The callback receives the compressed
     * Opus payload, the monotonic 32-bit sequence number for this
     * burst, and the capture-time timestamp in milliseconds (relative
     * to `performance.timeOrigin`). It must not retain `payload`
     * beyond the synchronous return — pass it to the transport
     * immediately.
     */
    onFrame: (payload: Uint8Array, sequence: number, timestampMs: number) => void;
    /**
     * Called once per captured 20 ms frame (regardless of transmit
     * state) with the RMS amplitude of that frame normalised to
     * `[0, 1]`. Drives a UI VU meter so the user can see the mic is
     * alive even before pressing PTT. Throttled to ~50 Hz by the
     * 20 ms frame cadence.
     */
    onLevel?: (rms: number) => void;
    /**
     * Optional per-frame gain provider. Called once per worklet frame;
     * the returned linear multiplier is applied in place to every
     * int16 sample (clamped to ±32767) before RMS calculation and
     * Opus encode. Wiring this as a callback rather than a constructor
     * option means the user can drag a gain slider without re-opening
     * the mic. Return `1.0` to leave samples untouched (fast-pathed).
     * Defaulted to unity when omitted.
     *
     * We disable the browser's built-in `autoGainControl` (see below)
     * because Chrome's AGC pumps on speech and ramps over multiple
     * seconds — wrong shape for short PTT bursts. A user-tunable
     * fixed multiplier compensates for quiet Android mics without
     * those artefacts.
     */
    getGain?: () => number;
    /**
     * Called if the worklet or the encoder errors. The capture handle
     * is left in a stopped state; the caller should destroy it and
     * decide whether to surface the error to the user.
     */
    onError?: (error: Error) => void;
}

/** Handle for controlling an active capture session. */
export interface CaptureHandle {
    /**
     * Open the transmit gate so subsequent worklet frames flow into
     * the encoder. The first call also resets the sequence counter
     * and capture-time clock so each PTT burst is self-contained on
     * the wire.
     */
    startTransmitting(): void;
    /** Close the transmit gate. Frames in flight are discarded. */
    stopTransmitting(): void;
    /**
     * Tear down the worklet, encoder, and underlying media stream.
     * Releases the microphone hardware. Idempotent.
     */
    destroy(): void;
}

/**
 * Open the microphone, build the worklet graph, and return a handle.
 *
 * Resolves once mic permission is granted and the worklet is wired
 * into a running `AudioContext`. The capture starts in the
 * non-transmitting state — call `startTransmitting()` when the user
 * presses PTT.
 */
export async function startCapture(opts: CaptureOptions): Promise<CaptureHandle> {
    // 1. Request the microphone. We disable the browser's built-in
    //    speech enhancements: the Opus encoder + our own anti-alias
    //    filter + the small-room voice-comms use case mean Chrome's
    //    aggressive AEC/AGC tend to do more harm than good. The user
    //    can re-enable in the OS if they need it.
    const audio: MediaTrackConstraints = {
        channelCount: 1,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
    };
    if (opts.deviceId !== undefined && opts.deviceId !== "") {
        // Use a non-`exact` constraint so a stale localStorage entry
        // (device unplugged since last session) falls back to the
        // default rather than throwing.
        audio.deviceId = opts.deviceId;
    }
    const stream = await navigator.mediaDevices.getUserMedia({
        audio,
        video: false,
    });

    // 2. AudioContext at the device's preferred rate. We do *not*
    //    force 16 kHz here — most browsers reject non-default rates,
    //    and the worklet does the resample anyway.
    const ctx = new AudioContext();
    try {
        await ctx.audioWorklet.addModule(new URL("/capture-processor.js", import.meta.url));
    } catch (e) {
        stream.getTracks().forEach((t) => t.stop());
        await ctx.close().catch(() => undefined);
        throw new Error(`failed to load capture worklet: ${(e as Error).message}`);
    }

    const source = ctx.createMediaStreamSource(stream);
    const node = new AudioWorkletNode(ctx, "nettacco-capture", {
        numberOfInputs: 1,
        numberOfOutputs: 0,
        channelCount: 1,
        channelCountMode: "explicit",
        channelInterpretation: "speakers",
    });
    source.connect(node);

    // 3. Construct the Opus encoder up front. Doing it lazily on the
    //    first `startTransmitting()` would push the WASM init cost
    //    onto the latency-critical PTT-press path.
    let encoder: OpusEncoder;
    try {
        encoder = await OpusEncoder.create();
    } catch (e) {
        node.disconnect();
        source.disconnect();
        stream.getTracks().forEach((t) => t.stop());
        await ctx.close().catch(() => undefined);
        throw e;
    }

    let transmitting = false;
    let sequence = 0;
    /**
     * `performance.now()` value at the moment the current burst began.
     * Used to derive the per-frame timestamp the wire format wants.
     */
    let burstStart = 0;

    /**
     * Compute the RMS amplitude of an int16 frame, normalised to
     * `[0, 1]`. Cheap (one pass, no allocations); called per frame.
     */
    const frameRms = (frame: Int16Array): number => {
        let sumSq = 0;
        for (let i = 0; i < frame.length; i++) {
            const v = frame[i]!;
            sumSq += v * v;
        }
        return Math.sqrt(sumSq / frame.length) / 32768;
    };

    node.port.onmessage = (ev: MessageEvent): void => {
        // The worklet only sends Int16Array frames or — never, in v1 —
        // would it send anything else. Defensive shape check kept
        // narrow.
        const data = ev.data as Int16Array | undefined;
        if (!(data instanceof Int16Array) || data.length !== OPUS_FRAME_SAMPLES) return;

        // Apply user-controlled gain in place. The buffer is the
        // structured-clone copy that arrived via postMessage — we own
        // it. Skip the loop entirely when gain ≈ 1 so the common case
        // pays no per-sample cost. Clamp into the int16 range; raw
        // assignment would silently wrap on overflow.
        const gain = opts.getGain?.() ?? 1.0;
        if (gain !== 1.0) {
            for (let i = 0; i < data.length; i++) {
                const v = Math.round(data[i]! * gain);
                data[i] = v < -32768 ? -32768 : v > 32767 ? 32767 : v;
            }
        }

        // Report level *after* gain so the VU meter reflects what the
        // encoder will actually see — this is the level the user is
        // calibrating against when they drag the gain slider.
        if (opts.onLevel !== undefined) {
            opts.onLevel(frameRms(data));
        }

        if (!transmitting) return;

        try {
            const payload = encoder.encode(data);
            const elapsed = performance.now() - burstStart;
            const timestampMs = Math.max(0, Math.floor(elapsed));
            opts.onFrame(payload, sequence, timestampMs);
            // 32-bit wrap-around matches the on-the-wire field width;
            // explicit mask keeps numbers small and clears any V8
            // "smi-overflow" surprises down the line.
            sequence = (sequence + 1) >>> 0;
        } catch (e) {
            opts.onError?.(e as Error);
            transmitting = false;
        }
    };

    node.onprocessorerror = (): void => {
        opts.onError?.(new Error("capture worklet aborted"));
        transmitting = false;
    };

    let destroyed = false;

    return {
        startTransmitting(): void {
            if (destroyed || transmitting) return;
            sequence = 0;
            burstStart = performance.now();
            transmitting = true;
        },
        stopTransmitting(): void {
            if (destroyed || !transmitting) return;
            transmitting = false;
        },
        destroy(): void {
            if (destroyed) return;
            destroyed = true;
            transmitting = false;
            node.port.onmessage = null;
            node.disconnect();
            source.disconnect();
            stream.getTracks().forEach((t) => t.stop());
            encoder.destroy();
            ctx.close().catch(() => undefined);
        },
    };
}

/** Re-export for callers that want to reason in frame-duration units. */
export { OPUS_FRAME_DURATION_MS };

/**
 * Briefly open and immediately close the microphone so the browser
 * caches the `getUserMedia` permission for this origin. Called from
 * the lobby's Connect-click handler — the click is the user gesture
 * iOS Safari requires for `getUserMedia`, and pre-granting at that
 * moment skips the permission popup on first PTT (when the user is
 * actually trying to talk).
 *
 * Tracks are stopped immediately on resolve, so the OS does not have
 * time to flip Bluetooth into HFP or iOS into earpiece routing — the
 * route stays in media mode throughout.
 *
 * Errors (denial, blocked permission, no device) are swallowed: the
 * first real PTT press will retry and surface a useful message via
 * the channel-screen `connState` line.
 */
export async function precacheMicPermission(): Promise<void> {
    let stream: MediaStream;
    try {
        stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                channelCount: 1,
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false,
            },
            video: false,
        });
    } catch {
        return;
    }
    stream.getTracks().forEach((t) => t.stop());
}
